import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";

const POLL_MS = 12000;

const timeAgo = (s) => {
  if (!s) return "";
  const d = (Date.now() - new Date(s).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)} min ago`;
  if (d < 86400) return `${Math.floor(d / 3600)} hr ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export const WhatsAppPendingCard = () => {
  const nav = useNavigate();
  const [data, setData] = useState({ count: 0, items: [] });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/pending");
      setData(data);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="mb-6" data-testid="wa-pending-card">
      <button
        onClick={() => nav("/whatsapp")}
        className="flex w-full items-center gap-3 rounded-lg border border-brand-line bg-white px-4 py-3 text-left transition-colors hover:bg-brand-tint/50"
        data-testid="wa-inbox-button"
      >
        <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <MessageCircle className="h-5 w-5" />
          {data.count > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white"
              data-testid="wa-unread-badge"
            >
              {data.count > 99 ? "99+" : data.count}
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-brand-ink">WhatsApp Inbox</span>
          <span className="block text-xs text-slate-500">
            {data.count > 0
              ? `${data.count} lead${data.count > 1 ? "s" : ""} awaiting your reply`
              : "All caught up — no leads waiting"}
          </span>
        </span>
        <span className="shrink-0 text-xs font-medium text-brand">Open →</span>
      </button>

      {data.items.length > 0 && (
        <Card className="mt-2 divide-y divide-slate-100 border-brand-line p-0 shadow-none" data-testid="wa-pending-list">
          {data.items.slice(0, 5).map((it) => (
            <button
              key={it.customer_id}
              onClick={() => nav(`/whatsapp?c=${it.customer_id}`)}
              className="flex w-full items-start gap-2 px-4 py-2.5 text-left transition-colors hover:bg-brand-tint/50"
              data-testid={`wa-pending-item-${it.customer_id}`}
            >
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
              <span className="min-w-0 flex-1">
                <span className="text-sm text-brand-ink">
                  <span className="font-medium">{it.name}</span>
                  <span className="text-slate-500">: {it.preview}</span>
                </span>
              </span>
              <span className="shrink-0 whitespace-nowrap text-[11px] text-slate-400">
                {timeAgo(it.last_ts)}
              </span>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
};

export default WhatsAppPendingCard;
